import React, { useState, useEffect, useCallback, useContext } from 'react';
import apiClient from '../api';
import AuthContext from '../context/AuthContext';
import AssociatedPersonForm from './AssociatedPersonForm';

function AssociatedPersons({ offenseId }) {
  const [associates, setAssociates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const { hasPermission } = useContext(AuthContext);

  const fetchAssociates = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(`/api/case/offenses/${offenseId}/associates`);
      setAssociates(response.data);
    } catch (err) {
      setError('Failed to fetch associated persons.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [offenseId]);

  useEffect(() => {
    fetchAssociates();
  }, [fetchAssociates]);
  
  const handleFormSubmit = async (formData) => {
    try {
      if (editingPerson) {
        await apiClient.put(`/api/case/associates/${editingPerson.id}`, formData);
      } else {
        await apiClient.post(`/api/case/offenses/${offenseId}/associates`, formData);
      }
      setIsModalOpen(false);
      setEditingPerson(null);
      fetchAssociates();
    } catch (err) {
      console.error('Failed to save associated person:', err);
      alert('Failed to save associated person.');
    }
  };

  const handleDelete = async (personId) => {
    if (window.confirm('Are you sure you want to delete this person?')) {
      try {
        await apiClient.delete(`/api/case/associates/${personId}`);
        fetchAssociates();
      } catch (err) {
        console.error('Failed to delete associated person:', err);
        alert('Failed to delete associated person.');
      }
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-300">
      <div className="flex justify-between items-center">
        <h3 className="text-md font-bold text-slate-700">Associated Persons</h3>
        {hasPermission('manage-associates') && (
          <button onClick={() => setIsModalOpen(true)} className="text-sm bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-1 px-3 rounded-lg">
            + Add Person
          </button>
        )}
      </div>
      <div className="mt-2 space-y-2">
        {isLoading && <p>Loading...</p>}
        {error && <p className="text-red-500">{error}</p>}
        {associates.length > 0 ? (
          associates.map(person => (
            <div key={person.id} className="text-sm text-slate-600 flex justify-between items-center p-2 rounded hover:bg-slate-100">
              <div>
                <span className="font-semibold">{person.full_name}</span> ({person.role})
              </div>
              {hasPermission('manage-associates') && (
                <div className="flex gap-2">
                  <button onClick={() => { setEditingPerson(person); setIsModalOpen(true); }} className="text-xs font-semibold hover:text-gold">EDIT</button>
                  <button onClick={() => handleDelete(person.id)} className="text-xs font-semibold hover:text-red-600">DELETE</button>
                </div>
              )}
            </div>
          ))
        ) : (
          !isLoading && <p className="text-sm text-slate-500">No associated persons recorded for this offense.</p>
        )}
      </div>

      {isModalOpen && (
        <AssociatedPersonForm
          initialData={editingPerson || {}}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingPerson(null); }}
        />
      )}
    </div>
  );
}

export default AssociatedPersons;